import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { PiggyBank, Cigarette, CalendarClock, TrendingDown, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Animated number counter
const CountUp = ({ value, duration = 900, decimals = 0, suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = Number(value) || 0;
    if (end === 0) {
      setDisplay(0);
      return;
    }
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return (
    <span>
      {decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString()}
      {suffix}
    </span>
  );
};

const ICONS = {
  footprints: Award,
  'calendar-days': CalendarClock,
  medal: Award,
  trophy: Award,
  'trending-down': TrendingDown,
  award: Award,
  star: Award,
  sparkles: Award,
  'piggy-bank': PiggyBank,
  gem: Award,
};

export const ProgressCard = ({ stats }) => {
  const { language } = useLanguage();
  if (!stats) return null;

  const {
    money_saved_eur = 0,
    total_cigarettes_avoided = 0,
    days_on_journey = 0,
    reduction_percent = 0,
    baseline_cigarettes_per_day = 0,
    current_cigarettes_per_day = 0,
    badges = [],
  } = stats;

  const hasBaseline = baseline_cigarettes_per_day > 0;

  return (
    <Card
      className="relative overflow-hidden rounded-3xl border-0 shadow-xl mb-6 animate-fade-in-up"
      data-testid="progress-card"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600" />
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.4),transparent_50%)]" />

      <div className="relative p-6 sm:p-8 text-white">
        <div className="flex items-center gap-2 mb-1 opacity-90">
          <TrendingDown className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">
            {language === 'fr' ? 'Vos progrès' : 'Your progress'}
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold mb-6">
          {language === 'fr' ? 'Chaque cigarette évitée compte' : 'Every avoided cigarette counts'}
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Money saved */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4" data-testid="stat-money-saved">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank className="w-4 h-4 opacity-80" />
              <span className="text-xs opacity-80">
                {language === 'fr' ? 'Économisé' : 'Saved'}
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black">
              <CountUp value={money_saved_eur} decimals={2} />
              <span className="text-lg ml-1 opacity-70">€</span>
            </div>
          </div>

          {/* Cigarettes avoided */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4" data-testid="stat-cigarettes-avoided">
            <div className="flex items-center gap-2 mb-2">
              <Cigarette className="w-4 h-4 opacity-80" />
              <span className="text-xs opacity-80">
                {language === 'fr' ? 'Cigarettes évitées' : 'Cigarettes avoided'}
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black">
              <CountUp value={total_cigarettes_avoided} />
            </div>
          </div>

          {/* Days on journey */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4" data-testid="stat-days-journey">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 opacity-80" />
              <span className="text-xs opacity-80">
                {language === 'fr' ? 'Jours de démarche' : 'Days on journey'}
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black">
              <CountUp value={days_on_journey} />
            </div>
          </div>

          {/* Reduction % */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4" data-testid="stat-reduction">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 opacity-80" />
              <span className="text-xs opacity-80">
                {language === 'fr' ? 'Réduction' : 'Reduction'}
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black">
              <CountUp value={Math.max(0, reduction_percent)} decimals={1} suffix="%" />
            </div>
            {hasBaseline && (
              <p className="text-[10px] opacity-70 mt-1">
                {baseline_cigarettes_per_day} → {current_cigarettes_per_day} {language === 'fr' ? 'cig/j' : 'cig/d'}
              </p>
            )}
          </div>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="mt-6" data-testid="badges-row">
            <p className="text-xs uppercase tracking-wider opacity-80 mb-3 font-semibold">
              {language === 'fr' ? 'Badges débloqués' : 'Badges unlocked'} ({badges.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => {
                const Icon = ICONS[b.icon] || Award;
                return (
                  <div
                    key={b.id}
                    className="flex items-center gap-1.5 bg-white/25 backdrop-blur-sm rounded-full px-3 py-1.5 hover:bg-white/35 transition-colors"
                    data-testid={`badge-${b.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold">
                      {language === 'fr' ? b.name_fr : b.name_en}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!hasBaseline && (
          <p className="text-sm mt-4 opacity-90">
            {language === 'fr'
              ? 'Complétez le dépistage pulmonaire pour voir vos économies.'
              : 'Complete the lung screening to see your savings.'}
          </p>
        )}
      </div>
    </Card>
  );
};
